import { EmptyState, ErrorState, FreshnessBar } from "../components/AdminPageStates";
import { useFounderJournal } from "../hooks/useFounderJournal";
import {
  FOUNDER_JOURNAL_IMMUTABILITY_POLICY,
  FounderJournalCorrection,
  FounderJournalEntry,
  FounderJournalMilestone,
  FounderJournalPeriodFilter,
  FounderJournalSummary
} from "../services/founderJournalService";

const PERIOD_FILTERS: Array<{ id: FounderJournalPeriodFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "today", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "milestones", label: "Milestones" }
];

function formatJournalDate(date: Date) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric"
  }).format(date);
}

function JournalSummaryBlock({ summary }: { summary: FounderJournalSummary }) {
  return (
    <section className="founder-journal-summary-block">
      <h4>{summary.title}</h4>
      <p>{summary.body}</p>
    </section>
  );
}

function MilestoneList({ milestones }: { milestones: FounderJournalMilestone[] }) {
  if (milestones.length === 0) return null;

  return (
    <section className="founder-journal-milestones" aria-label="Major Milestones">
      <p className="eyebrow">Major Milestones</p>
      {milestones.map((milestone) => (
        <article key={milestone.id}>
          <h4>{milestone.title}</h4>
          <p>{milestone.detail}</p>
        </article>
      ))}
    </section>
  );
}

function CorrectionNotes({ corrections }: { corrections: FounderJournalCorrection[] }) {
  if (corrections.length === 0) return null;

  return (
    <section className="founder-journal-corrections" aria-label="Correction Notes">
      <p className="eyebrow">Correction Notes</p>
      {corrections.map((correction) => (
        <p key={correction.id}>
          {correction.note}
          {correction.addedAt ? <small> {correction.addedAt.toLocaleDateString()}</small> : null}
        </p>
      ))}
    </section>
  );
}

function JournalEntryArticle({ entry }: { entry: FounderJournalEntry }) {
  return (
    <article className="founder-journal-entry" aria-label={`Journal entry for ${formatJournalDate(entry.date)}`}>
      <header className="founder-journal-entry-header">
        <div>
          <p className="eyebrow">{entry.dateKey}</p>
          <h2>{formatJournalDate(entry.date)}</h2>
        </div>
        <span className={`status-badge ${entry.founderPulse.status}`}>{entry.founderPulse.label}</span>
      </header>

      <section className="founder-journal-pulse" aria-label="Founder Pulse">
        <p>{entry.founderPulse.summary}</p>
      </section>

      <section className="founder-journal-story" aria-label="Today's Story">
        <p className="eyebrow">Today's Story</p>
        <ul>
          {entry.todaysStory.slice(0, 5).map((story) => (
            <li key={story}>{story}</li>
          ))}
        </ul>
      </section>

      <div className="founder-journal-summary-grid">
        <JournalSummaryBlock summary={entry.communitySummary} />
        <JournalSummaryBlock summary={entry.businessSummary} />
        <JournalSummaryBlock summary={entry.productHealth} />
        <JournalSummaryBlock summary={entry.infrastructureStatus} />
      </div>

      <MilestoneList milestones={entry.milestones} />

      <section className="founder-journal-reflection" aria-label="Founder's Reflection">
        <p className="eyebrow">Founder's Reflection</p>
        <blockquote>{entry.reflection}</blockquote>
      </section>

      <CorrectionNotes corrections={entry.corrections} />
    </article>
  );
}

export default function FounderJournal() {
  const {
    filteredEntries,
    duplicateDateKeys,
    error,
    isLoading,
    lastUpdated,
    period,
    searchTerm,
    jumpDate,
    setPeriod,
    setSearchTerm,
    setJumpDate,
    refresh
  } = useFounderJournal();

  if (error) {
    return <ErrorState message="Unable to load Founder's Journal. Check Firebase permissions and journal archive access." />;
  }

  return (
    <section className="founder-journal-page" aria-label="Founder's Journal">
      <FreshnessBar
        dataSource="Read-only immutable journal entries from founderJournalEntries"
        isLive={false}
        lastUpdated={lastUpdated}
        onRefresh={() => {
          void refresh();
        }}
      />

      <header className="founder-journal-hero">
        <p className="eyebrow">Founder's Journal</p>
        <h2>Chor's memory, one day at a time.</h2>
        <p>Revisit the story, milestones, health, and reflection preserved for each day in the life of the company.</p>
      </header>

      <section className="founder-journal-controls" aria-label="Journal navigation">
        <div className="founder-journal-filter-row">
          {PERIOD_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={period === filter.id ? "active" : ""}
              onClick={() => setPeriod(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <label>
          <span>Jump to date</span>
          <input type="date" value={jumpDate} onChange={(event) => setJumpDate(event.target.value)} />
        </label>
        <label>
          <span>Search</span>
          <input
            type="search"
            value={searchTerm}
            placeholder="Search date, milestone, coach, studio, dancer, opportunity, challenge..."
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </label>
      </section>

      {duplicateDateKeys.length > 0 ? (
        <section className="founder-journal-warning" aria-label="Duplicate journal entries">
          <strong>Duplicate journal entries detected.</strong>
          <p>{duplicateDateKeys.join(", ")} should be corrected by the journal finalizer. {FOUNDER_JOURNAL_IMMUTABILITY_POLICY}</p>
        </section>
      ) : null}

      {isLoading ? (
        <section className="founder-journal-entry loading-card" aria-busy="true">
          <div className="skeleton skeleton-title" />
          <div className="skeleton skeleton-note" />
          <div className="skeleton skeleton-note" />
        </section>
      ) : filteredEntries.length > 0 ? (
        <div className="founder-journal-timeline">
          {filteredEntries.map((entry) => (
            <JournalEntryArticle key={entry.id} entry={entry} />
          ))}
        </div>
      ) : (
        <EmptyState message="No journal entries match this view yet. Chor's history will appear here as daily entries are finalized." />
      )}
    </section>
  );
}
